import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { useAuth } from "../context/AuthContext";
import { AdminPermission } from "../api/generated/models/AdminPermission";
import { AdminAccountViewModel } from "../api/generated/models/AdminAccountViewModel";
import { Instant } from "../api/generated/models/Instant";
import { AdminAccountsService } from "../api/generated/services/AdminAccountsService";
import "../api/openApiConfig"; // Initialize OpenAPI configuration
import ActionsPopover from "../components/AdminManagement/ActionsPopover";
import AddAdminModal from "../components/AdminManagement/modals/AddAdminModal";
import UpdatePermissionsModal from "../components/AdminManagement/modals/UpdatePermissionsModal";
import ResendInvitationModal from "../components/AdminManagement/modals/ResendInvitationModal";
import DeleteAdminModal from "../components/AdminManagement/modals/DeleteAdminModal";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import { ADMIN_NAV, SUPER_ADMIN_LABEL } from "../config/adminNav";
import {
  collectPermissionGroups,
  labelPermissions,
  listPermissionOptions,
} from "../helpers/adminPermissions";

const PERMISSION_OPTIONS = listPermissionOptions(
  collectPermissionGroups(ADMIN_NAV),
  SUPER_ADMIN_LABEL
);

const AdminManagement: React.FC = () => {
  const { hasPermission } = useAuth();
  const [admins, setAdmins] = useState<AdminAccountViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] =
    useState<AdminAccountViewModel | null>(null);

  // Last login / last active are shown to Super Admins only.
  const isSuperAdmin = hasPermission(AdminPermission.SUPER_ADMIN);

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response =
        await AdminAccountsService.postApiAdminAccountsListAdminAccounts({});
      setAdmins(response);
      setError(null);
    } catch (err) {
      console.error("Error fetching admin accounts:", err);
      setError("Failed to load admin accounts. Please try again.");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleAddSuccess = () => {
    fetchAdmins();
    setSuccessMessage("Admin account created successfully. Invitation email sent.");
  };

  const handleUpdateSuccess = () => {
    fetchAdmins();
    setSuccessMessage("Admin permissions updated successfully.");
  };

  const handleResendSuccess = () => {
    fetchAdmins();
    setSuccessMessage("Invitation email resent successfully.");
  };

  const handleDeleteSuccess = () => {
    fetchAdmins();
    setSuccessMessage("Admin account removed successfully.");
  };

  const handleOpenUpdateModal = (admin: AdminAccountViewModel) => {
    setSelectedAdmin(admin);
    setIsUpdateModalOpen(true);
  };

  const handleOpenResendModal = (admin: AdminAccountViewModel) => {
    setSelectedAdmin(admin);
    setIsResendModalOpen(true);
  };

  const handleOpenDeleteModal = (admin: AdminAccountViewModel) => {
    setSelectedAdmin(admin);
    setIsDeleteModalOpen(true);
  };

  const renderTimestamp = (value?: Instant) => {
    if (!value) {
      return <span className="text-gray-400 text-sm">Never</span>;
    }
    return (
      <span className="text-sm whitespace-nowrap">
        {format(new Date(value as unknown as string), "dd MMM yyyy, HH:mm")}
      </span>
    );
  };

  const renderPermissionTags = (permissions: string[] | null | undefined) => {
    if (!permissions || permissions.length === 0) {
      return (
        <span className="text-gray-400 text-sm">No permissions</span>
      );
    }
    return (
      <div className="flex flex-wrap gap-1">
        {labelPermissions(permissions, PERMISSION_OPTIONS).map(({ key, label }) => (
          <span
            key={key}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  // Show full page loader only on initial load
  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className="flex max-md:flex-col md:items-center md:justify-between mb-10 max-md:gap-4">
        <h2 className="font-medium">Admins</h2>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          variant="light"
          className="gap-2"
        >
          Add Admin
          <PlusIcon />
        </Button>
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Show inline loader during subsequent data fetches */}
      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {/* Admin List */}
      {!loading && admins.length === 0 ? (
        <div className="text-center text-gray-500 my-8">
          No admin accounts found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-default">
            <thead>
              <tr>
                <th>Email</th>
                <th>Permissions</th>
                <th>Invitation Status</th>
                {isSuperAdmin && (
                  <>
                    <th>Last login</th>
                    <th>Last active</th>
                  </>
                )}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id}>
                  <td>{admin.email}</td>
                  <td className="max-w-md">
                    {renderPermissionTags(admin.adminPermissions)}
                  </td>
                  <td>
                    {admin.acceptedInvitation ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Accepted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Pending
                      </span>
                    )}
                  </td>
                  {isSuperAdmin && (
                    <>
                      <td>{renderTimestamp(admin.lastLoginAt)}</td>
                      <td>{renderTimestamp(admin.lastSeenAt)}</td>
                    </>
                  )}
                  <td className="text-right">
                    <ActionsPopover
                      onUpdatePermissions={() => handleOpenUpdateModal(admin)}
                      onResendInvitation={() => handleOpenResendModal(admin)}
                      onDelete={() => handleOpenDeleteModal(admin)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <AddAdminModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />

      <UpdatePermissionsModal
        isOpen={isUpdateModalOpen}
        admin={selectedAdmin}
        onClose={() => setIsUpdateModalOpen(false)}
        onSuccess={handleUpdateSuccess}
      />

      <ResendInvitationModal
        isOpen={isResendModalOpen}
        admin={selectedAdmin}
        onClose={() => setIsResendModalOpen(false)}
        onSuccess={handleResendSuccess}
      />

      <DeleteAdminModal
        isOpen={isDeleteModalOpen}
        admin={selectedAdmin}
        onClose={() => setIsDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default AdminManagement;
